import { useCallback, useEffect, useState } from "react";
import { API_CACHE_TTL, createApiCacheKey, fetchCachedApi, invalidateApiCache } from "@/utils/apiCache";

interface TrackPlayData {
  id: string;
  name: string;
  playCount: number;
  lastPlayed: number | null;
}

interface PopularTracksResponse {
  artist: string;
  tracks: TrackPlayData[];
  totalPlays: number;
}

interface UseTrackPlaysReturn {
  popularTracks: TrackPlayData[];
  totalPlays: number;
  isLoading: boolean;
  error: string | null;
  trackPlay: (trackId: string, trackName: string) => Promise<void>;
  refresh: () => void;
}

export default function useTrackPlays(artistName: string | undefined): UseTrackPlaysReturn {
  const [popularTracks, setPopularTracks] = useState<TrackPlayData[]>([]);
  const [totalPlays, setTotalPlays] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPopularTracks = useCallback(async () => {
    if (!artistName) return;

    setIsLoading(true);
    setError(null);

    try {
      const body = { action: "get", artist: artistName };
      const data = await fetchCachedApi<PopularTracksResponse>("/.netlify/functions/TrackPlays", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }, {
        ttlMs: API_CACHE_TTL.SIX_HOURS,
        shouldCache: (data) => Array.isArray(data?.tracks),
      });
      setPopularTracks(data.tracks);
      setTotalPlays(data.totalPlays);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setIsLoading(false);
    }
  }, [artistName]);

  const trackPlay = useCallback(async (trackId: string, trackName: string) => {
    if (!artistName) return;

    try {
      await fetch("/.netlify/functions/TrackPlays", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "track",
          artist: artistName,
          trackId,
          trackName,
        }),
      });
      invalidateApiCache(createApiCacheKey("/.netlify/functions/TrackPlays", { action: "get", artist: artistName }));

      setPopularTracks((prev) => {
        const existing = prev.find((t) => t.id === trackId);
        if (existing) {
          return prev
            .map((t) =>
              t.id === trackId
                ? { ...t, playCount: t.playCount + 1, lastPlayed: Date.now() }
                : t
            )
            .sort((a, b) => b.playCount - a.playCount);
        } else {
          return [
            ...prev,
            { id: trackId, name: trackName, playCount: 1, lastPlayed: Date.now() },
          ].sort((a, b) => b.playCount - a.playCount);
        }
      });
      setTotalPlays((prev) => prev + 1);
    } catch (err) {
      console.error("Error tracking play:", err);
    }
  }, [artistName]);

  useEffect(() => {
    fetchPopularTracks();
  }, [fetchPopularTracks]);

  return {
    popularTracks,
    totalPlays,
    isLoading,
    error,
    trackPlay,
    refresh: fetchPopularTracks,
  };
}
